// @ts-strict-ignore
import * as Sentry from '@sentry/react';

import {
  allProjectsClient,
  CurrentProject,
  currentProjectClient,
  Project,
} from 'common/report/queries/app-api/project-metadata-client';
import {getProjectId, setOrgId} from 'common/report/util';
import {ApiResource, ApiSingleValueResource} from 'common/util/model-resource/api-resource';
import {getFeatureRollout} from 'common/hooks/useFeatureRollout';
import {ProjectFeatureFlag} from 'common/report/constants';

import {FeatureRolloutName, FeatureRollouts} from '../util/feature-sampling';

export {CurrentProject, Project};

export class ProjectsResource extends ApiResource<Project> {
  apiClient = allProjectsClient;
}

type BaseRolloutTracking = {
  /** Human readable name for the experiment. */
  experimentName: string;
  /** Name for the control variant.
   *
   * This is the property value that will appear for users who are in the
   * control group of the experiment, i.e. users who did not get the feature.
   *
   * @default "Control"
   */
  controlVariantName?: string;
  /** Name for the test variant.
   *
   * This is the property value that will appear for users who are in the
   * test group of the experiment, i.e. users who did get the feature.
   *
   * @default "Test"
   */
  testVariantName?: string;
};

export enum RolloutType {
  Frontend = `frontend`,
  Backend = `backend`,
}

type FrontendRolloutTracking = BaseRolloutTracking & {
  rolloutType: RolloutType.Frontend;
  /** The rollout we want to track. */
  rolloutName: FeatureRolloutName;
};

type BackendRolloutTracking = BaseRolloutTracking & {
  rolloutType: RolloutType.Backend;
  featureFlag: ProjectFeatureFlag;
};

type RolloutTracking = FrontendRolloutTracking | BackendRolloutTracking;

/** The list of project level rollouts we want to track.
 *
 * We'll send an $experiment_started event for each rollout added here.
 * Then you can do breakdowns and filters in Mixpanel to monitor the impact of the rollout.
 */
const ROLLOUTS_TO_TRACK: ReadonlySet<RolloutTracking> = new Set<RolloutTracking>([
  {
    rolloutType: RolloutType.Frontend,
    rolloutName: FeatureRolloutName.BoardBreakdowns,
    experimentName: `Board Breakdowns`,
    controlVariantName: `Original Boards`,
    testVariantName: `With Board Breakdowns`,
  },
  {
    rolloutType: RolloutType.Frontend,
    rolloutName: FeatureRolloutName.OnboardingQuestionnaire,
    experimentName: `Onboarding Questionnaire`,
    controlVariantName: `No Questionnaire`,
    testVariantName: `With Questionnaire`,
  },
  {
    rolloutType: RolloutType.Backend,
    featureFlag: ProjectFeatureFlag.GsNewSearchSplit,
    experimentName: `Global Sidenav New Search Split`,
    controlVariantName: `Without Global Sidenav New Search Split`,
    testVariantName: `With Global Sidenav New Search Split`,
  },
]);

export class CurrentProjectResource extends ApiSingleValueResource<CurrentProject> {
  apiClient = currentProjectClient;

  constructor({projectId = null, urlManager = null} = {}) {
    super();
    this.id = projectId ?? getProjectId(urlManager);
    Sentry.setTag(`project_id`, this.id);

    // setup subscription to make project_id -> org_id mapping
    this.subscribe(() => {
      setOrgId({projectId: this.id, orgId: this.get().value.organizationId});

      // Send experiment started events for each rollout we want to track
      if (this.get().value.organizationId) {
        for (const rollout of ROLLOUTS_TO_TRACK.values()) {
          const {experimentName, controlVariantName, testVariantName} = {
            controlVariantName: `Control`,
            testVariantName: `Test`,
            ...rollout,
          };

          let isEnabled: boolean;
          if (rollout.rolloutType === RolloutType.Frontend) {
            const featureRollout = FeatureRollouts[rollout.rolloutName];
            const project = this.get().value;
            isEnabled = getFeatureRollout(project, featureRollout.name);
          } else {
            isEnabled = this.get().value.hasFeatureFlag(rollout.featureFlag);
          }

          mixpanel.track(`$experiment_started`, {
            'Experiment name': experimentName,
            'Variant name': isEnabled ? testVariantName : controlVariantName,
          });
        }
      }
    });
  }
}
